// src/components/Chat/Chat.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useChat } from '../../../contexts/ChatContext';
import { useCall } from '../../../contexts/CallContext';
import useAuth from '../../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import EmojiPicker from 'emoji-picker-react';
import {
    FaPaperPlane, FaUser, FaUsers, FaSearch, FaPlus, FaTimes,
    FaCircle, FaTrash, FaEdit, FaSmile, FaPaperclip, FaCheck,
    FaChevronLeft, FaEllipsisV, FaCommentDots, FaCog,
    FaReply, FaCopy, FaArrowDown, FaRegSmile, FaCheckCircle,
    FaRegClock, FaImage, FaFile, FaPhone, FaVideo, FaInfoCircle,
    FaArchive, FaBell, FaPinterest, FaUserCircle, FaHashtag,
    FaBellSlash, FaThumbtack, FaMicrophone, FaStop, FaShare,
    FaUserPlus
} from 'react-icons/fa';
import presenceService from '../../../services/PresenceService';
import styles from './Chat.module.css';

// Utility for localStorage
const saveToLocalStorage = (key, data) => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
        console.error('Error saving to localStorage:', e);
    }
};

const loadFromLocalStorage = (key) => {
    try {
        const data = localStorage.getItem(key);
        return data ? JSON.parse(data) : null;
    } catch (e) {
        console.error('Error loading from localStorage:', e);
        return null;
    }
};

const Chat = ({ onClose, isOpen, initialChannelId }) => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const { startCall } = useCall();
    const {
        channels,
        activeChannel,
        setActiveChannel,
        messages,
        users,
        sendMessage,
        createChannel,
        addUserToChannel,
        deleteMessage,
        editMessage,
        setTyping,
        getUserPresence,
        getUser,
        loading,
        typingUsers,
        isTyping: contextIsTyping,
        getTypingText,
        isUserTyping,
        addReaction,
        removeReaction,
        getChannelUnreadCount,
        markMessagesAsRead
    } = useChat();

    // ========== STATE DECLARATIONS ==========
    const [newMessage, setNewMessage] = useState('');
    const [showNewChannelModal, setShowNewChannelModal] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [channelName, setChannelName] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editText, setEditText] = useState('');
    const [showSidebar, setShowSidebar] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState({});
    const [presenceLoading, setPresenceLoading] = useState(true);
    const [notification, setNotification] = useState(null);
    const [replyTo, setReplyTo] = useState(null);
    const [searchMessageTerm, setSearchMessageTerm] = useState('');
    const [highlightedMessages, setHighlightedMessages] = useState([]);
    const [currentHighlightIndex, setCurrentHighlightIndex] = useState(-1);
    const [contextMenu, setContextMenu] = useState(null);
    const [showReactionPicker, setShowReactionPicker] = useState(null);
    const [isAtBottom, setIsAtBottom] = useState(true);
    const [messageStatuses, setMessageStatuses] = useState({});
    const [showMembersList, setShowMembersList] = useState(false);
    const [showChannelInfo, setShowChannelInfo] = useState(false);
    const [activeTab, setActiveTab] = useState('chats');
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    const [formattingMode, setFormattingMode] = useState(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    // Tier 2: Mute/Unmute state
    const [mutedChannels, setMutedChannels] = useState(() => {
        const saved = loadFromLocalStorage('mutedChannels');
        return saved || {};
    });

    // Tier 2: Pinned messages state
    const [pinnedMessages, setPinnedMessages] = useState(() => {
        const saved = loadFromLocalStorage('pinnedMessages');
        return saved || {};
    });

    // Tier 2: Forward modal state
    const [showForwardModal, setShowForwardModal] = useState(false);
    const [forwardingMessage, setForwardingMessage] = useState(null);

    // Tier 2: Member search state
    const [memberSearch, setMemberSearch] = useState('');

    // Add Members states
    const [showAddMembersModal, setShowAddMembersModal] = useState(false);
    const [availableUsersForAdd, setAvailableUsersForAdd] = useState([]);
    const [selectedUsersToAdd, setSelectedUsersToAdd] = useState([]);

    // Attachment states
    const [fileAttachments, setFileAttachments] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});

    // ========== REFS ==========
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const emojiPickerRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const profileMenuRef = useRef(null);
    const lastClickTimeRef = useRef(0);
    const clickCountRef = useRef(0);
    const scrollTimeoutRef = useRef(null);
    const isTypingRef = useRef(false);
    const draftsRef = useRef({});

    const channelsRef = useRef(channels);
    const hasProcessedInitialChannel = useRef(false);
    const hasMarkedMessagesAsRead = useRef(false);

    // ========== EFFECTS ==========
    // Tier 2: Save muted channels to localStorage
    useEffect(() => {
        saveToLocalStorage('mutedChannels', mutedChannels);
    }, [mutedChannels]);

    // Tier 2: Save pinned messages to localStorage
    useEffect(() => {
        saveToLocalStorage('pinnedMessages', pinnedMessages);
    }, [pinnedMessages]);

    useEffect(() => {
        channelsRef.current = channels;
    }, [channels]);

    useEffect(() => {
        if (messages.length > 0 && activeChannel && activeChannel.id && isOpen && !hasMarkedMessagesAsRead.current) {
            const timer = setTimeout(() => {
                markMessagesAsRead(activeChannel.id);
                hasMarkedMessagesAsRead.current = true;
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [messages, activeChannel, isOpen, markMessagesAsRead]);

    useEffect(() => {
        hasMarkedMessagesAsRead.current = false;
    }, [activeChannel?.id]);

    // Tier 1: Keyboard shortcuts for formatting
    useEffect(() => {
        const handleFormatShortcuts = (e) => {
            if (!inputRef.current || !inputRef.current.matches(':focus')) return;

            if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
                e.preventDefault();
                applyFormatting('bold');
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
                e.preventDefault();
                applyFormatting('italic');
            }
        };

        document.addEventListener('keydown', handleFormatShortcuts);
        return () => document.removeEventListener('keydown', handleFormatShortcuts);
    }, []);

    // Tier 1: Global ESC handler
    useEffect(() => {
        const handleGlobalEscape = (e) => {
            if (e.key === 'Escape') {
                setShowNewChannelModal(false);
                setShowMembersList(false);
                setContextMenu(null);
                setShowReactionPicker(null);
                setShowEmojiPicker(false);
                setShowChannelInfo(false);
                setShowProfileMenu(false);
                setShowForwardModal(false);
                setShowAddMembersModal(false);
                setFormattingMode(null);

                if (editingMessageId) {
                    setEditingMessageId(null);
                    setEditText('');
                    inputRef.current?.focus();
                }
            }
        };

        document.addEventListener('keydown', handleGlobalEscape);
        return () => document.removeEventListener('keydown', handleGlobalEscape);
    }, [editingMessageId]);

    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (mobile) {
                setShowSidebar(false);
            } else {
                setShowSidebar(true);
            }
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (!currentUser) return;
        setPresenceLoading(true);
        const unsubscribe = presenceService.subscribeToOnlineUsers((users) => {
            const onlineMap = {};
            users.forEach(user => {
                onlineMap[user.id] = user.online;
            });
            setOnlineUsers(onlineMap);
            setPresenceLoading(false);
        });
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [currentUser]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
            if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
                setShowProfileMenu(false);
            }
            if (formattingMode === 'open') {
                const dropdown = document.querySelector(`.${styles.formattingDropdown}`);
                if (dropdown && !dropdown.contains(event.target)) {
                    setFormattingMode(null);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [formattingMode]);

    useEffect(() => {
        const handleClickOutside = () => {
            setContextMenu(null);
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isAtBottom) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isAtBottom]);

    // Use ref for drafts to avoid re-renders
    useEffect(() => {
        if (activeChannel && activeChannel.id) {
            inputRef.current?.focus();
            const draft = draftsRef.current[activeChannel.id];
            if (draft) {
                setNewMessage(draft);
            } else {
                setNewMessage('');
            }
            setFileAttachments([]);
            setUploadProgress({});
            setShowChannelInfo(false);
        }
    }, [activeChannel]);

    // Update drafts using ref
    useEffect(() => {
        if (activeChannel && activeChannel.id && newMessage) {
            draftsRef.current = {
                ...draftsRef.current,
                [activeChannel.id]: newMessage
            };
        } else if (activeChannel && activeChannel.id && !newMessage) {
            const newDrafts = { ...draftsRef.current };
            delete newDrafts[activeChannel.id];
            draftsRef.current = newDrafts;
        }
    }, [newMessage, activeChannel]);

    useEffect(() => {
        if (initialChannelId && channels.length > 0 && !hasProcessedInitialChannel.current) {
            const channel = channels.find(c => c && c.id === initialChannelId);
            if (channel) {
                setActiveChannel(channel);
                if (window.innerWidth < 768) setShowSidebar(false);
                hasProcessedInitialChannel.current = true;
            }
        }
    }, [initialChannelId, channels, setActiveChannel]);

    useEffect(() => {
        hasProcessedInitialChannel.current = false;
    }, [initialChannelId]);

    // ========== CALLBACKS ==========
    const formatDateSeparator = useCallback((date) => {
        if (!date) return '';
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const targetDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        if (targetDate.getTime() === today.getTime()) {
            return 'Today';
        }
        if (targetDate.getTime() === yesterday.getTime()) {
            return 'Yesterday';
        }
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (targetDate > weekAgo) {
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            return days[date.getDay()];
        }
        if (date.getFullYear() === now.getFullYear()) {
            const months = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
            return `${months[date.getMonth()]} ${date.getDate()}`;
        }
        const months = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    }, []);

    const groupMessagesByDate = useCallback((messagesList) => {
        if (!messagesList || !Array.isArray(messagesList)) return {};
        const groups = {};
        messagesList.forEach(message => {
            if (!message || !message.timestamp) return;
            const date = new Date(message.timestamp);
            const dateKey = date.toDateString();
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(message);
        });
        return groups;
    }, []);

    // Memoize grouped messages to prevent unnecessary re-renders
    const groupedMessages = useMemo(() => {
        return groupMessagesByDate(messages);
    }, [messages, groupMessagesByDate]);

    // Memoize message keys to prevent recalculation on every render
    const messageKeys = useMemo(() => {
        const keys = [];
        Object.entries(groupedMessages).forEach(([dateKey, dateMessages]) => {
            dateMessages.forEach((message, msgIndex) => {
                keys.push({ message, showDateSeparator: msgIndex === 0 });
            });
        });
        return keys;
    }, [groupedMessages]);

    // Tier 1: Apply formatting function
    const applyFormatting = useCallback((type) => {
        if (!inputRef.current) return;

        const textarea = inputRef.current;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selected = newMessage.substring(start, end);

        if (!selected) {
            let placeholder = '';
            switch (type) {
                case 'bold':
                    placeholder = 'bold text';
                    break;
                case 'italic':
                    placeholder = 'italic text';
                    break;
                case 'code':
                    placeholder = 'code';
                    break;
                case 'strikethrough':
                    placeholder = 'strikethrough text';
                    break;
                default:
                    return;
            }

            const markers = {
                'bold': ['**', '**'],
                'italic': ['*', '*'],
                'code': ['`', '`'],
                'strikethrough': ['~~', '~~']
            };

            const [open, close] = markers[type];
            const newText = newMessage.substring(0, start) + open + placeholder + close + newMessage.substring(end);
            setNewMessage(newText);

            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + open.length, start + open.length + placeholder.length);
            }, 0);
            return;
        }

        const markers = {
            'bold': ['**', '**'],
            'italic': ['*', '*'],
            'code': ['`', '`'],
            'strikethrough': ['~~', '~~']
        };

        const [open, close] = markers[type];
        const formattedText = open + selected + close;
        const newText = newMessage.substring(0, start) + formattedText + newMessage.substring(end);
        setNewMessage(newText);

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + formattedText.length, start + formattedText.length);
        }, 0);
    }, [newMessage]);

    // Tier 1: Render formatted text - memoized
    const renderFormattedText = useCallback((text) => {
        if (!text) return null;

        let formatted = text
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/`(.*?)`/g, '<code>$1</code>')
            .replace(/~~(.*?)~~/g, '<del>$1</del>')
            .replace(/\n/g, '<br />');

        return <span dangerouslySetInnerHTML={{ __html: formatted }} />;
    }, []);

    // Tier 1: Scroll to bottom
    const scrollToBottom = useCallback((smooth = true) => {
        messagesEndRef.current?.scrollIntoView({
            behavior: smooth ? 'smooth' : 'auto'
        });
        setIsAtBottom(true);
        setShowScrollButton(false);
        setUnreadCount(0);

        if (activeChannel && activeChannel.id) {
            markMessagesAsRead(activeChannel.id);
            hasMarkedMessagesAsRead.current = true;
        }
    }, [activeChannel, markMessagesAsRead]);

    // Optimized scroll handler with requestAnimationFrame
    const handleScroll = useCallback(() => {
        if (scrollTimeoutRef.current) {
            cancelAnimationFrame(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = requestAnimationFrame(() => {
            if (!messagesContainerRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
            const atBottom = scrollHeight - scrollTop - clientHeight < 50;
            setIsAtBottom(atBottom);

            setShowScrollButton(!atBottom && messages.length > 0);

            if (!atBottom && messages.length > 0) {
                const container = messagesContainerRef.current;
                const messageElements = container.querySelectorAll('.message');
                let count = 0;
                for (let i = messageElements.length - 1; i >= 0; i--) {
                    const rect = messageElements[i].getBoundingClientRect();
                    if (rect.top < container.getBoundingClientRect().top) {
                        break;
                    }
                    if (!messageElements[i].classList.contains('ownMessage')) {
                        count++;
                    }
                }
                setUnreadCount(count);
            } else {
                setUnreadCount(0);
            }

            if (atBottom && activeChannel && activeChannel.id && isOpen && !hasMarkedMessagesAsRead.current) {
                markMessagesAsRead(activeChannel.id);
                hasMarkedMessagesAsRead.current = true;
            }
        });
    }, [activeChannel, isOpen, markMessagesAsRead, messages.length]);

    const handleChatInteraction = useCallback(() => {
        if (activeChannel && activeChannel.id && isOpen && !hasMarkedMessagesAsRead.current) {
            markMessagesAsRead(activeChannel.id);
            hasMarkedMessagesAsRead.current = true;
        }
    }, [activeChannel, isOpen, markMessagesAsRead]);

    // Optimized typing handler using ref
    const handleTyping = useCallback((e) => {
        const value = e.target.value;
        setNewMessage(value);

        if (activeChannel && activeChannel.id && currentUser) {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            // Use ref to track typing state
            if (value.length > 0 && !isTypingRef.current) {
                isTypingRef.current = true;
                setTyping(activeChannel.id, true);
            } else if (value.length === 0 && isTypingRef.current) {
                isTypingRef.current = false;
                setTyping(activeChannel.id, false);
            }

            if (value.length > 0) {
                typingTimeoutRef.current = setTimeout(() => {
                    isTypingRef.current = false;
                    setTyping(activeChannel.id, false);
                }, 2000);
            }
        }
    }, [activeChannel, currentUser, setTyping]);

    const handleFileSelect = useCallback((e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const maxSize = 10 * 1024 * 1024;
        const validFiles = files.filter(file => file.size <= maxSize);

        if (validFiles.length !== files.length) {
            setNotification('Some files exceed the 10MB limit');
            setTimeout(() => setNotification(null), 3000);
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp',
            'application/pdf', 'text/plain', 'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel'];

        const validTypes = validFiles.filter(file => {
            return allowedTypes.includes(file.type) || file.type.startsWith('image/');
        });

        if (validTypes.length === 0) {
            setNotification('File type not supported. Please upload images, PDFs, or documents.');
            setTimeout(() => setNotification(null), 3000);
            return;
        }

        setFileAttachments(prev => [...prev, ...validTypes]);
        e.target.value = '';
        inputRef.current?.focus();
    }, []);

    const removeFileAttachment = useCallback((index) => {
        setFileAttachments(prev => prev.filter((_, i) => i !== index));
        setUploadProgress(prev => {
            const newProgress = { ...prev };
            const keys = Object.keys(newProgress);
            if (keys[index]) {
                delete newProgress[keys[index]];
            }
            return newProgress;
        });
    }, []);

    const uploadFile = useCallback((file) => {
        return new Promise((resolve) => {
            let progress = 0;
            const interval = setInterval(() => {
                progress += 10 + Math.random() * 20;
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);
                    const url = URL.createObjectURL(file);
                    resolve({
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        url: url,
                    });
                }
                setUploadProgress(prev => ({
                    ...prev,
                    [file.name]: Math.min(progress, 100)
                }));
            }, 200);
        });
    }, []);

    const handleSendMessage = useCallback(async () => {
        if ((!newMessage.trim() && fileAttachments.length === 0) || !activeChannel || !activeChannel.id) return;
        if (uploadingFiles) return;

        if (activeChannel && activeChannel.id && !hasMarkedMessagesAsRead.current) {
            markMessagesAsRead(activeChannel.id);
            hasMarkedMessagesAsRead.current = true;
        }

        try {
            setUploadingFiles(true);

            const uploadedFiles = [];
            for (const file of fileAttachments) {
                const uploaded = await uploadFile(file);
                uploadedFiles.push(uploaded);
            }

            let messageText = newMessage.trim();
            let replyToId = replyTo?.id || null;

            const sentMessage = await sendMessage(
                activeChannel.id,
                messageText,
                uploadedFiles,
                replyToId
            );

            if (sentMessage && sentMessage.id) {
                setMessageStatuses(prev => ({
                    ...prev,
                    [sentMessage.id]: 'sent'
                }));
            }

            setNewMessage('');
            setFileAttachments([]);
            setUploadProgress({});
            setReplyTo(null);
            isTypingRef.current = false;
            if (activeChannel && activeChannel.id) {
                setTyping(activeChannel.id, false);
            }
            setShowEmojiPicker(false);
            setUploadingFiles(false);

            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            setIsAtBottom(true);
            setShowScrollButton(false);
        } catch (error) {
            console.error('Failed to send message:', error);
            setNotification('Failed to send message. Please try again.');
            setTimeout(() => setNotification(null), 3000);
            setUploadingFiles(false);
        }
    }, [newMessage, fileAttachments, activeChannel, uploadingFiles, replyTo, sendMessage, markMessagesAsRead, setTyping, uploadFile]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    }, [handleSendMessage]);

    const onEmojiClick = useCallback((emojiData, event) => {
        const emoji = emojiData?.emoji || emojiData?.native || '';
        if (emoji) {
            setNewMessage(prev => prev + emoji);
            inputRef.current?.focus();
        }
        setShowEmojiPicker(false);
    }, []);

    // Tier 2: Toggle mute channel
    const toggleMuteChannel = useCallback((channelId) => {
        setMutedChannels(prev => ({
            ...prev,
            [channelId]: !prev[channelId]
        }));
        setNotification(mutedChannels[channelId] ? 'Channel unmuted' : 'Channel muted');
        setTimeout(() => setNotification(null), 1500);
    }, [mutedChannels]);

    // Tier 2: Toggle pin message
    const togglePinMessage = useCallback((channelId, messageId) => {
        const key = `${channelId}-${messageId}`;
        setPinnedMessages(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
        setNotification(pinnedMessages[key] ? 'Message unpinned' : 'Message pinned');
        setTimeout(() => setNotification(null), 1500);
    }, [pinnedMessages]);

    // Tier 2: Forward message
    const handleForwardMessage = useCallback((message, channelId) => {
        if (!message || !channelId) return;

        const forwardText = `Forwarded: ${message.text || ''}`;
        sendMessage(channelId, forwardText, message.attachments || []);
        setShowForwardModal(false);
        setForwardingMessage(null);
        setNotification('Message forwarded!');
        setTimeout(() => setNotification(null), 2000);
    }, [sendMessage]);

    // Tier 2: Handle double click for quick reactions
    const handleMessageClick = useCallback((message, e) => {
        const now = Date.now();
        const timeSinceLastClick = now - lastClickTimeRef.current;

        if (timeSinceLastClick < 300) {
            clickCountRef.current++;
            if (clickCountRef.current === 2) {
                // Double click detected - add reaction
                if (message.senderId !== currentUser?.uid) {
                    handleReaction(message.id, '👍');
                    setNotification('Reacted with 👍');
                    setTimeout(() => setNotification(null), 1000);
                }
                clickCountRef.current = 0;
            }
        } else {
            clickCountRef.current = 1;
        }
        lastClickTimeRef.current = now;
    }, [currentUser]);

    const handleReaction = useCallback(async (messageId, emoji) => {
        if (!activeChannel || !activeChannel.id) return;
        try {
            await addReaction(activeChannel.id, messageId, emoji);
            setShowReactionPicker(null);
        } catch (error) {
            console.error('Failed to add reaction:', error);
        }
    }, [activeChannel, addReaction]);

    const handleReply = useCallback((message) => {
        setReplyTo(message);
        inputRef.current?.focus();
    }, []);

    const cancelReply = useCallback(() => {
        setReplyTo(null);
    }, []);

    const handleCopyMessage = useCallback((message) => {
        if (!message || !message.text) return;
        navigator.clipboard.writeText(message.text);
        setNotification('Message copied to clipboard');
        setTimeout(() => setNotification(null), 2000);
    }, []);

    // Handle message search
    const handleMessageSearch = useCallback((term) => {
        setSearchMessageTerm(term);
        if (!term.trim() || !messages || !Array.isArray(messages)) {
            setHighlightedMessages([]);
            setCurrentHighlightIndex(-1);
            return;
        }

        const results = messages
            .map((msg, index) => ({ msg, index }))
            .filter(({ msg }) =>
                msg && msg.text && msg.text.toLowerCase().includes(term.toLowerCase())
            );

        setHighlightedMessages(results);
        setCurrentHighlightIndex(results.length > 0 ? 0 : -1);

        if (results.length > 0) {
            const element = document.getElementById(`message-${results[0].msg.id}`);
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element?.classList.add(styles.highlightedMessage);
            setTimeout(() => {
                element?.classList.remove(styles.highlightedMessage);
            }, 3000);
        }
    }, [messages]);

    const navigateHighlight = useCallback((direction) => {
        if (highlightedMessages.length === 0) return;

        const newIndex = (currentHighlightIndex + direction + highlightedMessages.length) % highlightedMessages.length;
        setCurrentHighlightIndex(newIndex);

        const element = document.getElementById(`message-${highlightedMessages[newIndex].msg.id}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element?.classList.add(styles.highlightedMessage);
        setTimeout(() => {
            element?.classList.remove(styles.highlightedMessage);
        }, 3000);
    }, [highlightedMessages, currentHighlightIndex]);

    const handleCreateChannel = useCallback(async () => {
        if (!channelName.trim() || selectedUsers.length === 0) return;

        try {
            const userIds = selectedUsers.map(u => u.uid);
            const newChannel = await createChannel(channelName.trim(), userIds, true);
            setShowNewChannelModal(false);
            setChannelName('');
            setSelectedUsers([]);
            setActiveChannel(newChannel);
            if (isMobile) setShowSidebar(false);
        } catch (error) {
            console.error('Failed to create channel:', error);
        }
    }, [channelName, selectedUsers, createChannel, setActiveChannel, isMobile]);

    // Handle adding members to group
    const handleAddMembersToGroup = useCallback(async () => {
        if (!activeChannel || !activeChannel.id || selectedUsersToAdd.length === 0) return;

        try {
            for (const user of selectedUsersToAdd) {
                await addUserToChannel(activeChannel.id, user.uid);
            }
            setShowAddMembersModal(false);
            setSelectedUsersToAdd([]);
            setSearchTerm('');
            setNotification(`${selectedUsersToAdd.length} member(s) added to group`);
            setTimeout(() => setNotification(null), 2000);
        } catch (error) {
            console.error('Failed to add members:', error);
            setNotification('Failed to add members');
            setTimeout(() => setNotification(null), 3000);
        }
    }, [activeChannel, selectedUsersToAdd, addUserToChannel]);

    const handleDeleteMessage = useCallback(async (messageId) => {
        if (!activeChannel || !activeChannel.id || !messageId) return;
        if (!window.confirm(t('chat.confirmDelete'))) return;

        try {
            await deleteMessage(activeChannel.id, messageId);
            setContextMenu(null);
        } catch (error) {
            console.error('Failed to delete message:', error);
        }
    }, [activeChannel, deleteMessage, t]);

    const handleEditMessage = useCallback(async (messageId) => {
        if (!editText.trim() || !activeChannel || !activeChannel.id) return;

        try {
            await editMessage(activeChannel.id, messageId, editText.trim());
            setEditingMessageId(null);
            setEditText('');
            setContextMenu(null);
            inputRef.current?.focus();
        } catch (error) {
            console.error('Failed to edit message:', error);
        }
    }, [editText, activeChannel, editMessage]);

    const startEditing = useCallback((message) => {
        setEditingMessageId(message.id);
        setEditText(message.text);
        setContextMenu(null);
    }, []);

    const toggleUserSelection = useCallback((user) => {
        setSelectedUsers(prev => {
            if (prev.some(u => u.uid === user.uid)) {
                return prev.filter(u => u.uid !== user.uid);
            } else {
                return [...prev, user];
            }
        });
    }, []);

    const filteredUsers = useMemo(() => {
        if (!users) return [];
        return users.filter(user =>
            user && user.uid !== currentUser?.uid &&
            (user.nome || user.email).toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [users, currentUser, searchTerm]);

    const formatTime = useCallback((date) => {
        if (!date) return '';
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m`;
        if (minutes < 1440) return `${Math.floor(minutes / 60)}h`;
        if (minutes < 10080) return `${Math.floor(minutes / 1440)}d`;
        return date.toLocaleDateString();
    }, []);

    const formatTimeShort = useCallback((date) => {
        if (!date) return '';
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }, []);

    const getUserOnlineStatus = useCallback((userId) => {
        if (!userId) return false;
        return onlineUsers[userId] === true;
    }, [onlineUsers]);

    const getStatusColor = useCallback((userId) => {
        if (getUserOnlineStatus(userId)) {
            return '#22C55E';
        }
        return '#94A3B8';
    }, [getUserOnlineStatus]);

    const getChannelDisplayName = useCallback((channel) => {
        if (!channel) return '';

        const memberCount = channel.members?.length || 0;
        const isGroupChat = memberCount > 2;

        if (isGroupChat) {
            if (channel.name && channel.name.trim()) {
                return channel.name;
            }
            return 'Group Chat';
        }

        if (channel.isPrivate || memberCount <= 2) {
            const otherMember = channel.members?.find(id => id !== currentUser?.uid);
            const otherUser = users ? users.find(u => u && u.uid === otherMember) : null;
            return otherUser?.nome || otherUser?.email || 'Unknown User';
        }

        return channel.name || 'Chat';
    }, [currentUser, users]);

    const getChatSubtitle = useCallback((channel) => {
        if (!channel) return '';

        const memberCount = channel.members?.length || 0;
        const isGroupChat = memberCount > 2;

        if (channel.isPrivate || memberCount <= 2) {
            const otherMember = channel.members?.find(id => id !== currentUser?.uid);
            const isOnline = getUserOnlineStatus(otherMember);
            const isTyping = otherMember && channel.id ? isUserTyping(channel.id, otherMember) : false;
            if (isTyping) return 'Typing...';
            return isOnline ? 'Online' : 'Offline';
        }

        if (isGroupChat && channel.members && channel.members.length > 0) {
            const memberNames = channel.members
                .filter(id => id !== currentUser?.uid)
                .map(id => {
                    const user = users ? users.find(u => u && u.uid === id) : null;
                    return user?.nome || user?.email || 'Unknown';
                })
                .slice(0, 3);

            let subtitle = memberNames.join(', ');
            if (channel.members.length > 4) {
                subtitle += ` and ${channel.members.length - 3} others`;
            }
            return subtitle;
        }
        return '';
    }, [currentUser, users, getUserOnlineStatus, isUserTyping]);

    const getChannelAvatar = useCallback((channel) => {
        if (!channel) return null;

        const memberCount = channel.members?.length || 0;
        const isGroupChat = memberCount > 2;

        if (isGroupChat) {
            return channel.name?.[0]?.toUpperCase() || '👥';
        }

        if (channel.isPrivate || memberCount <= 2) {
            const otherMember = channel.members?.find(id => id !== currentUser?.uid);
            const otherUser = users ? users.find(u => u && u.uid === otherMember) : null;
            return otherUser?.nome?.[0]?.toUpperCase() || otherUser?.email?.[0]?.toUpperCase() || '?';
        }

        return channel.name?.[0]?.toUpperCase() || '👥';
    }, [currentUser, users]);

    const getMessageStatusIcon = useCallback((status) => {
        switch (status) {
            case 'sending':
                return { icon: FaRegClock, className: 'sending', color: '#94A3B8' };
            case 'sent':
                return { icon: FaCheck, className: 'sent', color: '#94A3B8' };
            case 'delivered':
                return { icon: FaCheckCircle, className: 'delivered', color: '#94A3B8' };
            case 'read':
                return { icon: FaCheckCircle, className: 'read', color: '#22C55E' };
            case 'error':
                return { icon: FaTimes, className: 'error', color: '#EF4444' };
            default:
                return { icon: FaRegClock, className: 'sending', color: '#94A3B8' };
        }
    }, []);

    const getChannelStatusText = useCallback((channel) => {
        if (!channel) return '';

        const memberCount = channel.members?.length || 0;
        const isGroupChat = memberCount > 2;

        if (channel.isPrivate || memberCount <= 2) {
            const otherMember = channel.members?.find(id => id !== currentUser?.uid);
            if (getUserOnlineStatus(otherMember)) {
                const isTyping = otherMember && channel.id ? isUserTyping(channel.id, otherMember) : false;
                return isTyping ? 'Typing...' : 'Online';
            }
            return 'Offline';
        }

        if (isGroupChat && channel.members && channel.members.length > 0) {
            return `${channel.members.length} members`;
        }
        return '';
    }, [currentUser, getUserOnlineStatus, isUserTyping]);

    const getLastMessageTime = useCallback((channel) => {
        if (!channel) return '';
        const channelMessages = messages?.filter(m => m.channelId === channel.id) || [];
        if (channelMessages.length === 0) return '';
        const lastMsg = channelMessages[channelMessages.length - 1];
        if (!lastMsg) return '';
        return formatTime(new Date(lastMsg.timestamp));
    }, [messages, formatTime]);

    // Memoize pinned messages for current channel
    const pinnedMessagesList = useMemo(() => {
        if (!activeChannel || !messages) return [];
        return messages.filter(m =>
            pinnedMessages[`${activeChannel.id}-${m.id}`]
        );
    }, [activeChannel, messages, pinnedMessages]);

    // ============================================
    // VOICE CALL HANDLER
    // ============================================
    const handleStartVoiceCall = useCallback(() => {
        if (!activeChannel) {
            console.warn('No active channel for voice call');
            return;
        }

        // Verify if it's a 1v1 conversation
        const memberCount = activeChannel.members?.length || 0;
        if (memberCount === 2 || activeChannel.isPrivate) {
            const otherMemberId = activeChannel.members?.find(id => id !== currentUser?.uid);
            const otherUser = users.find(u => u.uid === otherMemberId);

            if (otherUser) {
                try {
                    startCall(otherUser, activeChannel.id);
                } catch (err) {
                    console.error('Failed to start voice call:', err);
                    setNotification('Unable to start call. Please check microphone permissions.');
                    setTimeout(() => setNotification(null), 3000);
                }
            } else {
                setNotification('Unable to find other user for call');
                setTimeout(() => setNotification(null), 2000);
            }
        } else {
            setNotification('Voice calls are only available for 1-on-1 conversations');
            setTimeout(() => setNotification(null), 3000);
        }
    }, [activeChannel, currentUser, users, startCall]);

    // ========== RENDER FUNCTIONS ==========
    const renderFileAttachments = useCallback(() => {
        if (fileAttachments.length === 0) return null;

        return (
            <div className={styles.attachmentsPreview}>
                {fileAttachments.map((file, index) => {
                    const isImage = file.type?.startsWith('image/');
                    const progress = uploadProgress[file.name] || 0;
                    const fileSize = (file.size / 1024).toFixed(1);

                    return (
                        <div key={`${file.name}-${index}`} className={styles.attachmentItem}>
                            {isImage ? (
                                <div className={styles.attachmentImage}>
                                    <img
                                        src={URL.createObjectURL(file)}
                                        alt={file.name}
                                        className={styles.attachmentThumb}
                                    />
                                </div>
                            ) : (
                                <div className={styles.attachmentIcon}>
                                    <FaFile className={styles.fileIcon} />
                                </div>
                            )}
                            <div className={styles.attachmentInfo}>
                                <span className={styles.attachmentName}>{file.name}</span>
                                <span className={styles.attachmentSize}>{fileSize} KB</span>
                                {uploadingFiles && progress < 100 && (
                                    <div className={styles.progressBar}>
                                        <div
                                            className={styles.progressFill}
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>
                                )}
                            </div>
                            <button
                                className={styles.attachmentRemove}
                                onClick={() => removeFileAttachment(index)}
                                disabled={uploadingFiles}
                            >
                                <FaTimes />
                            </button>
                        </div>
                    );
                })}
            </div>
        );
    }, [fileAttachments, uploadProgress, uploadingFiles, removeFileAttachment]);

    const renderMessageAttachments = useCallback((attachments) => {
        if (!attachments || attachments.length === 0) return null;

        return (
            <div className={styles.messageAttachments}>
                {attachments.map((attachment, idx) => (
                    <div key={idx} className={styles.messageAttachment}>
                        {attachment.type?.startsWith('image/') ? (
                            <img
                                src={attachment.url}
                                alt={attachment.name}
                                className={styles.messageAttachmentImage}
                                onClick={() => window.open(attachment.url, '_blank')}
                            />
                        ) : (
                            <div className={styles.messageAttachmentFile}>
                                <FaFile className={styles.messageAttachmentIcon} />
                                <div className={styles.messageAttachmentInfo}>
                                    <span className={styles.messageAttachmentName}>
                                        {attachment.name}
                                    </span>
                                    <span className={styles.messageAttachmentSize}>
                                        {(attachment.size / 1024).toFixed(1)} KB
                                    </span>
                                </div>
                                <button
                                    className={styles.messageAttachmentDownload}
                                    onClick={() => window.open(attachment.url, '_blank')}
                                >
                                    Download
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    }, []);

    // FIXED: renderMessage with no useMemo hooks inside
    const renderMessage = useCallback((message, showDateSeparator) => {
        if (!message) return null;
        const isOwn = message.senderId === currentUser?.uid;
        const isEditing = editingMessageId === message.id;
        const messageIndex = messages ? messages.indexOf(message) : -1;
        const isFirstInGroup = messageIndex === 0 ||
            (messages && messages[messageIndex - 1]?.senderId !== message.senderId);
        const isHighlighted = highlightedMessages.some(h => h.msg.id === message.id);
        const reactions = message.reactions || {};
        const replyToMessage = message.replyTo && messages ? messages.find(m => m && m.id === message.replyTo) : null;
        const isPinned = pinnedMessages[`${activeChannel?.id}-${message.id}`];

        // Direct computations (not useMemo since they're simple)
        const messageText = message.text ? renderFormattedText(message.text) : null;
        const attachments = renderMessageAttachments(message.attachments);

        return (
            <React.Fragment key={message.id || Math.random()}>
                {showDateSeparator && message.timestamp && (
                    <div className={styles.dateSeparator}>
                        <span className={styles.dateSeparatorText}>
                            {formatDateSeparator(new Date(message.timestamp))}
                        </span>
                    </div>
                )}
                <div
                    id={`message-${message.id}`}
                    className={`${styles.message} ${isOwn ? styles.ownMessage : styles.otherMessage} ${isHighlighted ? styles.highlightedMessage : ''} ${isPinned ? styles.pinnedMessageItem : ''}`}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        setContextMenu({
                            x: e.clientX,
                            y: e.clientY,
                            message
                        });
                    }}
                    onClick={(e) => handleMessageClick(message, e)}
                >
                    {isPinned && (
                        <div className={styles.pinnedIndicator}>
                            <FaThumbtack className={styles.pinnedIcon} />
                        </div>
                    )}
                    {!isOwn && isFirstInGroup && (
                        <div className={styles.messageAvatar}>
                            <div className={styles.avatarPlaceholder}>
                                <span>{message.senderName?.[0]?.toUpperCase() || '?'}</span>
                            </div>
                        </div>
                    )}
                    {!isOwn && !isFirstInGroup && (
                        <div className={styles.messageAvatarSpacer} />
                    )}
                    <div className={styles.messageContent}>
                        {!isOwn && isFirstInGroup && (
                            <div className={styles.messageHeader}>
                                <span className={styles.senderName}>{message.senderName}</span>
                                <span
                                    className={styles.messageTime}
                                    title={message.timestamp ? new Date(message.timestamp).toLocaleString() : ''}
                                >
                                    {formatTimeShort(new Date(message.timestamp))}
                                </span>
                            </div>
                        )}

                        {replyToMessage && (
                            <div className={styles.repliedMessage}>
                                <FaReply className={styles.replyIcon} />
                                <span className={styles.repliedSender}>{replyToMessage.senderName}:</span>
                                <span className={styles.repliedText}>{replyToMessage.text?.substring(0, 60) || ''}...</span>
                            </div>
                        )}

                        {isEditing ? (
                            <div className={styles.editContainer}>
                                <input
                                    type="text"
                                    value={editText}
                                    onChange={(e) => setEditText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleEditMessage(message.id);
                                        if (e.key === 'Escape') {
                                            setEditingMessageId(null);
                                            setEditText('');
                                            inputRef.current?.focus();
                                        }
                                    }}
                                    className={styles.editInput}
                                    autoFocus
                                    placeholder="Edit message..."
                                />
                                <div className={styles.editActions}>
                                    <button onClick={() => handleEditMessage(message.id)} className={styles.saveEditBtn}>
                                        Save
                                    </button>
                                    <button
                                        onClick={() => {
                                            setEditingMessageId(null);
                                            setEditText('');
                                            inputRef.current?.focus();
                                        }}
                                        className={styles.cancelEditBtn}
                                    >
                                        Cancel (ESC)
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className={`${styles.messageBubble} ${isOwn ? styles.ownBubble : styles.otherBubble}`}>
                                    {message.text && (
                                        <p className={styles.messageText}>
                                            {messageText}
                                        </p>
                                    )}

                                    {attachments}

                                    {message.edited && (
                                        <span className={styles.editedTag}>edited</span>
                                    )}

                                    {isOwn && (
                                        <span
                                            className={styles.messageTimeOwn}
                                            title={message.timestamp ? new Date(message.timestamp).toLocaleString() : ''}
                                        >
                                            {formatTimeShort(new Date(message.timestamp))}
                                        </span>
                                    )}
                                </div>

                                {Object.keys(reactions).length > 0 && (
                                    <div className={styles.reactionsContainer}>
                                        {Object.entries(reactions).map(([emoji, userIds]) => (
                                            <button
                                                key={emoji}
                                                className={`${styles.reactionBadge} ${userIds && userIds.includes(currentUser?.uid) ? styles.userReacted : ''}`}
                                                onClick={() => handleReaction(message.id, emoji)}
                                            >
                                                {emoji} {userIds ? userIds.length : 0}
                                            </button>
                                        ))}
                                        <button
                                            className={styles.addReactionBtn}
                                            onClick={() => setShowReactionPicker(showReactionPicker === message.id ? null : message.id)}
                                        >
                                            <FaRegSmile />
                                        </button>
                                    </div>
                                )}

                                {showReactionPicker === message.id && (
                                    <div className={styles.reactionPicker}>
                                        {['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '🎉'].map(emoji => (
                                            <button
                                                key={emoji}
                                                className={styles.reactionOption}
                                                onClick={() => handleReaction(message.id, emoji)}
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                        <button
                                            className={styles.reactionOption}
                                            onClick={() => setShowReactionPicker(null)}
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                )}

                                {isOwn && messageStatuses[message.id] && (
                                    <div className={styles.messageStatus}>
                                        {messageStatuses[message.id] === 'sending' && <FaRegClock className={styles.sending} />}
                                        {messageStatuses[message.id] === 'sent' && <FaCheck className={styles.sent} />}
                                        {messageStatuses[message.id] === 'delivered' && <FaCheckCircle className={styles.delivered} />}
                                        {messageStatuses[message.id] === 'read' && <FaCheckCircle className={styles.read} />}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </React.Fragment>
        );
    }, [
        currentUser,
        editingMessageId,
        messages,
        highlightedMessages,
        pinnedMessages,
        activeChannel,
        formatDateSeparator,
        formatTimeShort,
        handleMessageClick,
        handleReaction,
        renderFormattedText,
        renderMessageAttachments,
        showReactionPicker,
        messageStatuses,
        handleEditMessage,
        editText
    ]);

    // Memoize the message list
    const messageList = useMemo(() => {
        if (!messages || messages.length === 0) {
            return null;
        }
        return messageKeys.map(({ message, showDateSeparator }) =>
            renderMessage(message, showDateSeparator)
        );
    }, [messages, messageKeys, renderMessage]);

    // Tier 2: Render members list with search and add members button
    const renderMembersList = useCallback(() => {
        if (!showMembersList || !activeChannel) return null;

        const memberCount = activeChannel.members?.length || 0;
        const isGroupChat = memberCount > 2;
        const isCreator = activeChannel.createdBy === currentUser?.uid;

        if (!isGroupChat) return null;

        const onlineCount = activeChannel.members?.filter(id => getUserOnlineStatus(id)).length || 0;

        const filteredMembers = activeChannel.members?.filter(id => {
            if (!memberSearch) return true;
            const user = users ? users.find(u => u && u.uid === id) : null;
            if (!user) return false;
            const name = (user.nome || user.email || '').toLowerCase();
            return name.includes(memberSearch.toLowerCase());
        }) || [];

        return (
            <div className={styles.modalOverlay} onClick={() => setShowMembersList(false)}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h3>Participants</h3>
                        <button
                            className={styles.modalClose}
                            onClick={() => setShowMembersList(false)}
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <div className={styles.modalBody}>
                        <div className={styles.memberStats}>
                            <span>{memberCount} members</span>
                            <span className={styles.onlineCount}>● {onlineCount} online</span>
                        </div>

                        <div className={styles.modalSearch}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search members..."
                                value={memberSearch}
                                onChange={(e) => setMemberSearch(e.target.value)}
                                className={styles.searchInput}
                            />
                            {memberSearch && (
                                <button
                                    className={styles.clearSearchBtn}
                                    onClick={() => setMemberSearch('')}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>

                        {/* Add Members button for creator */}
                        {isCreator && (
                            <button
                                className={styles.addMembersBtn}
                                onClick={() => {
                                    setShowMembersList(false);
                                    setShowAddMembersModal(true);
                                    // Filter users not already in the channel
                                    const available = users.filter(u =>
                                        u && u.uid !== currentUser?.uid &&
                                        !activeChannel.members?.includes(u.uid)
                                    );
                                    setAvailableUsersForAdd(available);
                                    setSelectedUsersToAdd([]);
                                    setSearchTerm('');
                                }}
                            >
                                <FaUserPlus /> Add Members
                            </button>
                        )}

                        <div className={styles.membersList}>
                            {filteredMembers.length > 0 ? (
                                filteredMembers.map(id => {
                                    const user = users ? users.find(u => u && u.uid === id) : null;
                                    const isOnline = getUserOnlineStatus(id);
                                    const isCurrentUser = id === currentUser?.uid;
                                    const isCreatorUser = activeChannel.createdBy === id;

                                    return (
                                        <div key={id} className={styles.memberItem}>
                                            <div className={styles.memberAvatar}>
                                                <span>{user?.nome?.[0] || user?.email?.[0] || '?'}</span>
                                            </div>
                                            <div className={styles.memberInfo}>
                                                <span className={styles.memberName}>
                                                    {user?.nome || user?.email || 'Unknown User'}
                                                    {isCurrentUser && <span className={styles.youTag}> (You)</span>}
                                                    {isCreatorUser && <span className={styles.creatorTag}> 👑</span>}
                                                </span>
                                                <span className={`${styles.memberStatus} ${isOnline ? styles.online : ''}`}>
                                                    {isOnline ? 'Online' : 'Offline'}
                                                </span>
                                            </div>
                                            {isOnline && (
                                                <FaCircle style={{ color: '#22C55E', fontSize: '0.6rem' }} />
                                            )}
                                        </div>
                                    );
                                })
                            ) : (
                                <div className={styles.noMembersFound}>
                                    <p>No members found matching "{memberSearch}"</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.modalFooter}>
                        <button
                            className={styles.cancelBtn}
                            onClick={() => setShowMembersList(false)}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }, [showMembersList, activeChannel, currentUser, users, memberSearch, getUserOnlineStatus]);

    // Tier 2: Render add members modal
    const renderAddMembersModal = useCallback(() => {
        if (!showAddMembersModal) return null;

        const filteredAvailable = availableUsersForAdd.filter(user => {
            if (!searchTerm) return true;
            const name = (user.nome || user.email || '').toLowerCase();
            return name.includes(searchTerm.toLowerCase());
        });

        return (
            <div className={styles.modalOverlay} onClick={() => setShowAddMembersModal(false)}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h3>Add Members to Group</h3>
                        <button
                            className={styles.modalClose}
                            onClick={() => setShowAddMembersModal(false)}
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <div className={styles.modalBody}>
                        <div className={styles.modalSearch}>
                            <FaSearch className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search users..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                            {searchTerm && (
                                <button
                                    className={styles.clearSearchBtn}
                                    onClick={() => setSearchTerm('')}
                                >
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                        <div className={styles.userList}>
                            {filteredAvailable.length > 0 ? (
                                filteredAvailable.map(user => (
                                    <div
                                        key={user.uid}
                                        className={`${styles.userItem} ${selectedUsersToAdd.some(u => u.uid === user.uid) ? styles.selectedUser : ''}`}
                                        onClick={() => {
                                            setSelectedUsersToAdd(prev => {
                                                if (prev.some(u => u.uid === user.uid)) {
                                                    return prev.filter(u => u.uid !== user.uid);
                                                } else {
                                                    return [...prev, user];
                                                }
                                            });
                                        }}
                                    >
                                        <div className={styles.userAvatar}>
                                            <span>{user.nome?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}</span>
                                        </div>
                                        <span className={styles.userName}>{user.nome || user.email}</span>
                                        {getUserOnlineStatus(user.uid) && (
                                            <FaCircle style={{ color: '#22C55E', fontSize: '0.6rem' }} />
                                        )}
                                        {selectedUsersToAdd.some(u => u.uid === user.uid) && (
                                            <FaCheck className={styles.selectedCheck} />
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className={styles.noUsersFound}>
                                    <p>{searchTerm ? 'No users found matching your search' : 'No users available to add'}</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.modalFooter}>
                        <button
                            className={styles.cancelBtn}
                            onClick={() => setShowAddMembersModal(false)}
                        >
                            Cancel
                        </button>
                        <button
                            className={styles.createBtn}
                            onClick={handleAddMembersToGroup}
                            disabled={selectedUsersToAdd.length === 0}
                        >
                            Add {selectedUsersToAdd.length > 0 && `(${selectedUsersToAdd.length})`}
                        </button>
                    </div>
                </div>
            </div>
        );
    }, [showAddMembersModal, availableUsersForAdd, searchTerm, selectedUsersToAdd, getUserOnlineStatus, handleAddMembersToGroup]);

    // Tier 2: Render forward modal
    const renderForwardModal = useCallback(() => {
        if (!showForwardModal || !forwardingMessage) return null;

        return (
            <div className={styles.modalOverlay} onClick={() => setShowForwardModal(false)}>
                <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h3>Forward Message</h3>
                        <button
                            className={styles.modalClose}
                            onClick={() => setShowForwardModal(false)}
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <div className={styles.modalBody}>
                        <div className={styles.forwardPreview}>
                            <FaShare className={styles.forwardIcon} />
                            <div>
                                <div className={styles.forwardLabel}>Forwarding:</div>
                                <div className={styles.forwardText}>
                                    "{forwardingMessage.text?.substring(0, 100) || 'Message without text'}"
                                    {forwardingMessage.text?.length > 100 ? '...' : ''}
                                </div>
                                {forwardingMessage.attachments?.length > 0 && (
                                    <div className={styles.forwardAttachments}>
                                        📎 {forwardingMessage.attachments.length} attachment(s)
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={styles.forwardChannelList}>
                            <div className={styles.forwardSearch}>
                                <FaSearch className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Search channels..."
                                    className={styles.searchInput}
                                    onChange={(e) => {
                                        const search = e.target.value.toLowerCase();
                                        const items = document.querySelectorAll('.forwardChannelItem');
                                        items.forEach(item => {
                                            const name = item.dataset.name?.toLowerCase() || '';
                                            item.style.display = name.includes(search) ? 'flex' : 'none';
                                        });
                                    }}
                                />
                            </div>
                            {channels?.map(channel => {
                                if (channel.id === activeChannel?.id) return null;
                                const displayName = getChannelDisplayName(channel);
                                const avatar = getChannelAvatar(channel);
                                const isMuted = mutedChannels[channel.id];

                                return (
                                    <div
                                        key={channel.id}
                                        className={`${styles.channelItem} ${styles.forwardChannelItem}`}
                                        data-name={displayName.toLowerCase()}
                                        onClick={() => {
                                            handleForwardMessage(forwardingMessage, channel.id);
                                        }}
                                    >
                                        <div className={styles.channelAvatar}>
                                            <span>{avatar}</span>
                                        </div>
                                        <div className={styles.channelInfo}>
                                            <span className={styles.channelName}>{displayName}</span>
                                            {isMuted && <span className={styles.mutedIndicator}>🔇</span>}
                                        </div>
                                        <button className={styles.forwardSelectBtn}>
                                            <FaPaperPlane />
                                        </button>
                                    </div>
                                );
                            })}
                            {(!channels || channels.length === 0) && (
                                <div className={styles.noChannelsFound}>
                                    <p>No channels available</p>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className={styles.modalFooter}>
                        <button
                            className={styles.cancelBtn}
                            onClick={() => setShowForwardModal(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }, [showForwardModal, forwardingMessage, channels, activeChannel, getChannelDisplayName, getChannelAvatar, mutedChannels, handleForwardMessage]);

    const renderSidebarTabs = useCallback(() => {
        return (
            <div className={styles.sidebarTabs}>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'chats' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('chats')}
                >
                    <FaCommentDots />
                    <span>Chats</span>
                </button>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'files' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('files')}
                >
                    <FaFile />
                    <span>Files</span>
                </button>
                <button
                    className={`${styles.tabBtn} ${activeTab === 'links' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('links')}
                >
                    <FaHashtag />
                    <span>Links</span>
                </button>
            </div>
        );
    }, [activeTab]);

    const renderChannelItem = useCallback((channel) => {
        if (!channel) return null;
        const isActive = activeChannel?.id === channel.id;
        const displayName = getChannelDisplayName(channel);
        const avatar = getChannelAvatar(channel);
        const memberCount = channel.members?.length || 0;
        const isGroupChat = memberCount > 2;
        const otherMember = channel.members?.find(id => id !== currentUser?.uid);
        const statusColor = otherMember ? getStatusColor(otherMember) : '#94A3B8';
        const isOnline = otherMember ? getUserOnlineStatus(otherMember) : false;
        const isTyping = otherMember && channel.id ? isUserTyping(channel.id, otherMember) : false;
        const subtitle = getChatSubtitle(channel);
        const lastMsgTime = getLastMessageTime(channel);
        const unreadCount = getChannelUnreadCount?.(channel.id) || 0;
        const isMuted = mutedChannels[channel.id];

        return (
            <div
                key={channel.id || Math.random()}
                className={`${styles.channelItem} ${isActive ? styles.activeChannel : ''} ${isMuted ? styles.mutedChannel : ''}`}
                onClick={() => {
                    setActiveChannel(channel);
                    if (isMobile) setShowSidebar(false);
                }}
            >
                <div className={styles.channelAvatarWrapper}>
                    <div className={`${styles.channelAvatar} ${isGroupChat ? styles.groupAvatar : ''}`}>
                        {isGroupChat ? (
                            <span className={styles.groupAvatarText}>{avatar}</span>
                        ) : (
                            <span className={styles.avatarText}>{avatar}</span>
                        )}
                    </div>
                    {!isGroupChat && otherMember && (
                        <span
                            className={`${styles.statusDot} ${isOnline ? styles.online : ''}`}
                            style={{ backgroundColor: statusColor }}
                        />
                    )}
                    {isMuted && (
                        <span className={styles.mutedBadge}>🔇</span>
                    )}
                </div>
                <div className={styles.channelInfo}>
                    <div className={styles.channelNameRow}>
                        <span className={styles.channelName}>
                            {displayName}
                            {isMuted && <span className={styles.mutedIndicator}>🔇</span>}
                        </span>
                    </div>
                </div>
            </div>
        );
    }, [
        activeChannel,
        getChannelDisplayName,
        getChannelAvatar,
        currentUser,
        getStatusColor,
        getUserOnlineStatus,
        isUserTyping,
        getChatSubtitle,
        getLastMessageTime,
        getChannelUnreadCount,
        mutedChannels,
        isMobile,
        setActiveChannel
    ]);

    const renderTypingIndicator = useCallback(() => {
        if (!activeChannel || !activeChannel.id) return null;

        const typingText = getTypingText(activeChannel.id);
        if (!typingText) return null;

        return (
            <div className={styles.typingIndicator}>
                <div className={styles.typingDots}>
                    <span></span><span></span><span></span>
                </div>
                <span className={styles.typingText}>{typingText}</span>
            </div>
        );
    }, [activeChannel, getTypingText]);

    if (!isOpen) return null;

    return (
        <div className={styles.chatContainer}>
            {/* Notification toast */}
            {notification && (
                <div className={styles.notificationToast}>
                    <span>{notification}</span>
                </div>
            )}

            {/* Context Menu */}
            {contextMenu && contextMenu.message && (
                <div
                    className={styles.contextMenu}
                    style={{
                        top: Math.min(contextMenu.y, window.innerHeight - 200),
                        left: Math.min(contextMenu.x, window.innerWidth - 200)
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button onClick={() => { handleReply(contextMenu.message); setContextMenu(null); }}>
                        <FaReply /> Reply
                    </button>
                    <button onClick={() => { handleCopyMessage(contextMenu.message); setContextMenu(null); }}>
                        <FaCopy /> Copy
                    </button>
                    <button onClick={() => {
                        setForwardingMessage(contextMenu.message);
                        setShowForwardModal(true);
                        setContextMenu(null);
                    }}>
                        <FaShare /> Forward
                    </button>
                    <button onClick={() => {
                        togglePinMessage(activeChannel?.id, contextMenu.message.id);
                        setContextMenu(null);
                    }}>
                        <FaThumbtack />
                        {pinnedMessages[`${activeChannel?.id}-${contextMenu.message.id}`]
                            ? 'Unpin' : 'Pin'}
                    </button>
                    {contextMenu.message.senderId === currentUser?.uid && (
                        <>
                            <button onClick={() => { startEditing(contextMenu.message); setContextMenu(null); }}>
                                <FaEdit /> Edit
                            </button>
                            <button onClick={() => { handleDeleteMessage(contextMenu.message.id); setContextMenu(null); }}>
                                <FaTrash /> Delete
                            </button>
                        </>
                    )}
                    <button onClick={() => { setContextMenu(null); }}>
                        <FaTimes /> Cancel
                    </button>
                </div>
            )}

            {/* Sidebar */}
            {(showSidebar || !activeChannel) && (
                <div className={`${styles.sidebar} ${!showSidebar ? styles.sidebarHidden : ''}`}>
                    {/* Sidebar Header */}
                    <div className={styles.sidebarHeader}>
                        <div className={styles.sidebarTitle}>
                            <span>Chat</span>
                        </div>
                        <div className={styles.sidebarActions}>
                            <button
                                className={styles.sidebarActionBtn}
                                onClick={() => setShowNewChannelModal(true)}
                                title="New conversation"
                            >
                                <FaPlus />
                            </button>
                        </div>
                        {isMobile && (
                            <button
                                className={styles.closeSidebarBtn}
                                onClick={() => setShowSidebar(false)}
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    {/* User Profile - Top */}
                    <div className={styles.sidebarUserProfile}>
                        <div className={styles.sidebarUserAvatar}>
                            <span>{currentUser?.nome?.[0]?.toUpperCase() || currentUser?.email?.[0]?.toUpperCase() || 'U'}</span>
                        </div>
                        <div className={styles.sidebarUserInfo}>
                            <span className={styles.sidebarUserName}>
                                {currentUser?.nome || currentUser?.email || 'User'}
                            </span>
                            <span className={`${styles.sidebarUserStatus} ${getUserOnlineStatus(currentUser?.uid) ? styles.online : ''}`}>
                                {getUserOnlineStatus(currentUser?.uid) ? 'Online' : 'Offline'}
                            </span>
                        </div>
                        <div className={styles.sidebarUserActions}>
                            <button className={styles.sidebarUserActionBtn}>
                                <FaUserCircle />
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    {renderSidebarTabs()}

                    {/* Search */}
                    <div className={styles.searchContainer}>
                        <FaSearch className={styles.searchIcon} />
                        <input
                            type="text"
                            placeholder="Search conversations..."
                            className={styles.searchInput}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Channel List */}
                    <div className={styles.channelList}>
                        {channels && channels.length > 0 ? (
                            channels
                                .filter(channel => {
                                    if (!channel) return false;
                                    if (!searchTerm) return true;
                                    const displayName = getChannelDisplayName(channel);
                                    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
                                })
                                .map(renderChannelItem)
                        ) : !loading ? (
                            <div className={styles.noChannels}>
                                <FaCommentDots className={styles.noChannelsIcon} />
                                <p>No conversations yet</p>
                                <button
                                    className={styles.createChannelBtn}
                                    onClick={() => setShowNewChannelModal(true)}
                                >
                                    Start a new chat
                                </button>
                            </div>
                        ) : (
                            <div className={styles.loadingChannels}>
                                <div className={styles.spinner}></div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar Footer - Archive */}
                    <div className={styles.sidebarFooter}>
                        <button className={styles.archiveBtn}>
                            <FaArchive />
                            <span>Archived</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Chat Main */}
            <div className={styles.chatMain} onClick={handleChatInteraction}>
                {activeChannel ? (
                    <>
                        {/* Chat Header */}
                        <div className={styles.chatHeader}>
                            <div className={styles.chatHeaderLeft}>
                                {isMobile && !showSidebar && (
                                    <button
                                        className={styles.backBtn}
                                        onClick={() => setShowSidebar(true)}
                                    >
                                        <FaChevronLeft />
                                    </button>
                                )}
                                <div className={styles.headerAvatar}>
                                    <span className={styles.headerAvatarText}>{getChannelAvatar(activeChannel)}</span>
                                    {!activeChannel.members?.length > 2 && activeChannel.isPrivate && (
                                        <span
                                            className={`${styles.headerStatusDot} ${getUserOnlineStatus(activeChannel.members?.find(id => id !== currentUser?.uid)) ? styles.online : ''}`}
                                            style={{
                                                backgroundColor: getStatusColor(
                                                    activeChannel.members?.find(id => id !== currentUser?.uid)
                                                )
                                            }}
                                        />
                                    )}
                                </div>
                                <div className={styles.headerInfo}>
                                    <span
                                        className={styles.headerName}
                                        onClick={() => {
                                            const memberCount = activeChannel.members?.length || 0;
                                            if (memberCount > 2) {
                                                setShowMembersList(true);
                                            }
                                        }}
                                        style={{ cursor: (activeChannel.members?.length || 0) > 2 ? 'pointer' : 'default' }}
                                    >
                                        {getChannelDisplayName(activeChannel)}
                                        {(activeChannel.members?.length || 0) > 2 && (
                                            <span className={styles.headerMemberCount}>
                                                ({activeChannel.members.length})
                                            </span>
                                        )}
                                        {mutedChannels[activeChannel.id] && (
                                            <span className={styles.headerMutedIndicator}>🔇</span>
                                        )}
                                    </span>
                                    <span className={`${styles.headerStatus} ${activeChannel.isPrivate && isUserTyping(activeChannel.id, activeChannel.members?.find(id => id !== currentUser?.uid)) ? styles.typing : ''}`}>
                                        {getChannelStatusText(activeChannel)}
                                    </span>
                                </div>
                            </div>
                            <div className={styles.chatHeaderRight}>
                                {/* VOICE CALL BUTTON */}
                                <button
                                    className={styles.headerActionBtn}
                                    title="Voice call"
                                    onClick={handleStartVoiceCall}
                                >
                                    <FaPhone />
                                </button>
                                <button className={styles.headerActionBtn} title="Video call">
                                    <FaVideo />
                                </button>
                                <button
                                    className={`${styles.headerActionBtn} ${showChannelInfo ? styles.active : ''}`}
                                    onClick={() => setShowChannelInfo(!showChannelInfo)}
                                    title="Channel info"
                                >
                                    <FaInfoCircle />
                                </button>
                            </div>
                        </div>

                        {/* Channel Info Panel */}
                        {showChannelInfo && (
                            <div className={styles.channelInfoPanel}>
                                <div className={styles.channelInfoContent}>
                                    <div className={styles.channelInfoAvatar}>
                                        <span>{getChannelAvatar(activeChannel)}</span>
                                    </div>
                                    <h4>{getChannelDisplayName(activeChannel)}</h4>
                                    <p className={styles.channelInfoDesc}>
                                        {activeChannel.members?.length || 0} members
                                    </p>
                                    <div className={styles.channelInfoActions}>
                                        <button
                                            className={styles.channelInfoAction}
                                            onClick={() => toggleMuteChannel(activeChannel.id)}
                                        >
                                            {mutedChannels[activeChannel.id] ? <FaBellSlash /> : <FaBell />}
                                            {mutedChannels[activeChannel.id] ? 'Unmute' : 'Mute'}
                                        </button>
                                        <button
                                            className={styles.channelInfoAction}
                                            onClick={() => {
                                                const count = pinnedMessagesList.length;
                                                setNotification(`${count} pinned messages`);
                                                setTimeout(() => setNotification(null), 2000);
                                            }}
                                        >
                                            <FaPinterest /> Pin ({pinnedMessagesList.length})
                                        </button>
                                        <button
                                            className={styles.channelInfoAction}
                                            onClick={() => setShowMembersList(true)}
                                        >
                                            <FaUser /> Members
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Messages */}
                        <div
                            className={styles.messagesContainer}
                            ref={messagesContainerRef}
                            onScroll={handleScroll}
                        >
                            {/* Tier 2: Pinned messages section */}
                            {pinnedMessagesList.length > 0 && (
                                <div className={styles.pinnedMessagesContainer}>
                                    <div className={styles.pinnedHeader}>
                                        <FaThumbtack className={styles.pinnedHeaderIcon} />
                                        Pinned Messages ({pinnedMessagesList.length})
                                    </div>
                                    {pinnedMessagesList.map(msg => (
                                        <div key={msg.id} className={styles.pinnedMessage}>
                                            <div className={styles.pinnedMessageContent}>
                                                <span className={styles.pinnedMessageSender}>{msg.senderName}:</span>
                                                <span className={styles.pinnedMessageText}>
                                                    {msg.text?.substring(0, 60) || 'Message without text'}
                                                    {msg.text?.length > 60 ? '...' : ''}
                                                </span>
                                            </div>
                                            <button
                                                className={styles.unpinBtn}
                                                onClick={() => togglePinMessage(activeChannel.id, msg.id)}
                                                title="Unpin message"
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!messages || messages.length === 0 ? (
                                <div className={styles.emptyMessages}>
                                    <div className={styles.emptyIconContainer}>
                                        <FaCommentDots className={styles.emptyIcon} />
                                    </div>
                                    <h4>No messages yet</h4>
                                    <p>Start the conversation by sending a message</p>
                                </div>
                            ) : (
                                messageList
                            )}
                            <div ref={messagesEndRef} />

                            {/* Tier 1: Scroll to bottom button */}
                            {showScrollButton && (
                                <div className={styles.scrollToBottomWrapper}>
                                    <button
                                        className={styles.scrollToBottomBtn}
                                        onClick={() => scrollToBottom(true)}
                                        title="Scroll to bottom"
                                    >
                                        <FaArrowDown />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Typing Indicator */}
                        {renderTypingIndicator()}

                        {/* Reply Indicator */}
                        {replyTo && (
                            <div className={styles.replyIndicator}>
                                <div className={styles.replyContent}>
                                    <FaReply className={styles.replyIcon} />
                                    <span className={styles.replyLabel}>Replying to {replyTo.senderName}</span>
                                    <span className={styles.replyPreview}>"{replyTo.text?.substring(0, 50) || ''}..."</span>
                                </div>
                                <button onClick={cancelReply} className={styles.cancelReplyBtn}>
                                    <FaTimes />
                                </button>
                            </div>
                        )}

                        {/* File Attachments Preview */}
                        {renderFileAttachments()}

                        {/* Input Area */}
                        <div className={styles.inputArea}>
                            <div className={styles.inputWrapper}>
                                <div className={styles.inputActions}>
                                    <label className={styles.attachmentBtn} title="Attach file">
                                        <FaPaperclip />
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            onChange={handleFileSelect}
                                            className={styles.fileInputHidden}
                                            accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx"
                                            disabled={uploadingFiles}
                                        />
                                    </label>

                                    {/* Formatting Dropdown */}
                                    <div className={styles.formattingDropdownWrapper}>
                                        <button
                                            className={styles.formattingToggleBtn}
                                            onClick={() => setFormattingMode(formattingMode === 'open' ? null : 'open')}
                                            title="Text formatting"
                                            type="button"
                                        >
                                            <FaPinterest />
                                        </button>
                                        {formattingMode === 'open' && (
                                            <div className={styles.formattingDropdown}>
                                                <button
                                                    onClick={() => { applyFormatting('bold'); setFormattingMode(null); }}
                                                    className={styles.formatBtn}
                                                    title="Bold (Ctrl+B)"
                                                >
                                                    <strong>B</strong>
                                                </button>
                                                <button
                                                    onClick={() => { applyFormatting('italic'); setFormattingMode(null); }}
                                                    className={styles.formatBtn}
                                                    title="Italic (Ctrl+I)"
                                                >
                                                    <em>I</em>
                                                </button>
                                                <button
                                                    onClick={() => { applyFormatting('code'); setFormattingMode(null); }}
                                                    className={styles.formatBtn}
                                                    title="Code"
                                                >
                                                    {'<>'}
                                                </button>
                                                <button
                                                    onClick={() => { applyFormatting('strikethrough'); setFormattingMode(null); }}
                                                    className={styles.formatBtn}
                                                    title="Strikethrough"
                                                >
                                                    <s>S</s>
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.emojiWrapper} ref={emojiPickerRef}>
                                        <button
                                            className={styles.emojiBtn}
                                            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            title="Emoji"
                                            type="button"
                                        >
                                            <FaSmile />
                                        </button>
                                        {showEmojiPicker && (
                                            <div className={styles.emojiPickerContainer}>
                                                <EmojiPicker
                                                    onEmojiClick={onEmojiClick}
                                                    width={320}
                                                    height={400}
                                                    previewConfig={{
                                                        showPreview: true,
                                                        defaultEmoji: '😊',
                                                        defaultCaption: 'What\'s Your Mood?'
                                                    }}
                                                    skinTonesDisabled={false}
                                                    searchDisabled={false}
                                                    searchPlaceholder="Search emojis..."
                                                    theme="light"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <textarea
                                    ref={inputRef}
                                    value={newMessage}
                                    onChange={handleTyping}
                                    onKeyDown={handleKeyPress}
                                    placeholder={replyTo ? `Reply to ${replyTo.senderName}...` : "Type a message..."}
                                    className={styles.messageInput}
                                    rows={1}
                                    disabled={uploadingFiles}
                                />
                                <button
                                    onClick={handleSendMessage}
                                    className={styles.sendBtn}
                                    disabled={(!newMessage.trim() && fileAttachments.length === 0) || uploadingFiles}
                                >
                                    {uploadingFiles ? (
                                        <div className={styles.sendingSpinner} />
                                    ) : (
                                        <FaPaperPlane />
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className={styles.noChannelSelected}>
                        <div className={styles.noChannelIconContainer}>
                            <FaCommentDots className={styles.noChannelIcon} />
                        </div>
                        <h3>Welcome to Chat</h3>
                        <p>Select a conversation from the sidebar to start messaging</p>
                        {(!channels || channels.length === 0) && !loading && (
                            <button
                                className={styles.createChannelBtn}
                                onClick={() => setShowNewChannelModal(true)}
                                style={{ marginTop: '16px' }}
                            >
                                Start a new conversation
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* New Channel Modal */}
            {showNewChannelModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h3>New Conversation</h3>
                            <button
                                className={styles.modalClose}
                                onClick={() => setShowNewChannelModal(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div className={styles.formGroup}>
                                <label>Conversation name</label>
                                <input
                                    type="text"
                                    value={channelName}
                                    onChange={(e) => setChannelName(e.target.value)}
                                    placeholder="Enter conversation name..."
                                    className={styles.modalInput}
                                />
                            </div>
                            <div className={styles.formGroup}>
                                <label>Add members</label>
                                <div className={styles.userSearch}>
                                    <FaSearch className={styles.searchIcon} />
                                    <input
                                        type="text"
                                        placeholder="Search users..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={styles.searchInput}
                                    />
                                </div>
                                <div className={styles.userList}>
                                    {filteredUsers.length > 0 ? (
                                        filteredUsers.map(user => (
                                            <div
                                                key={user.uid}
                                                className={`${styles.userItem} ${selectedUsers.some(u => u.uid === user.uid) ? styles.selectedUser : ''}`}
                                                onClick={() => toggleUserSelection(user)}
                                            >
                                                <div className={styles.userAvatar}>
                                                    <span>{user.nome?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}</span>
                                                </div>
                                                <span className={styles.userName}>{user.nome || user.email}</span>
                                                {getUserOnlineStatus(user.uid) && (
                                                    <FaCircle style={{ color: '#22C55E', fontSize: '0.6rem' }} />
                                                )}
                                                {selectedUsers.some(u => u.uid === user.uid) && (
                                                    <FaCheck className={styles.selectedCheck} />
                                                )}
                                            </div>
                                        ))
                                    ) : (
                                        <div className={styles.noUsersFound}>
                                            <p>No users found</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button
                                className={styles.cancelBtn}
                                onClick={() => setShowNewChannelModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.createBtn}
                                onClick={handleCreateChannel}
                                disabled={!channelName.trim() || selectedUsers.length === 0}
                            >
                                Create
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tier 2: Members List Modal with search and add members */}
            {renderMembersList()}

            {/* Tier 2: Add Members Modal */}
            {renderAddMembersModal()}

            {/* Tier 2: Forward Modal */}
            {renderForwardModal()}
        </div>
    );
};

export default React.memo(Chat);